<script lang="ts">import Chevron from '../Chevron/Chevron.svelte';
import Flyout from '../Flyout/Flyout.svelte';
import Toggle from '../Cart/Toggle/Toggle.svelte';
import { GLOBAL_NAV_DESKTOP_CART_CONTENT_ID, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION, } from '../../../../js-v2/constants';
import { activeDropdownStore } from '../../../../js-v2/stores/desktop-nav-stores';
import { activeFlyoutStore } from '../../../../js-v2/stores/flyout-stores';
import { blurOnClick } from '../../../../js-v2/blurOnClick';
import { hoverWithDelay } from '../../../../js-v2/hoverWithDelay';
import { onClickOutside } from '../../../../js-v2/onClickOutside';
import { onTouchOrClick } from '../../../../js-v2/onTouchOrClick';
import { getTracking } from '../../../../js-v2/tracking';
import { getLocation, getNavDeviceType } from '../../../../js-v2/trackingUtils';
import { getUrlPath } from '../../../../js-v2/urlUtils';
const { id = '', title = '', alignment = 'left', enableScrim = false, enableChevron = false, renderCartButton = false, children, } = $props();
let flyoutToggleEl = $state();
const trackFn = getTracking();
// Variables
const isOpen = $derived($activeFlyoutStore?.id === id);
// Open the flyout
function open() {
    if (isOpen) {
        return;
    }
    activeFlyoutStore.open({
        id,
        enableScrim,
    });
    activeDropdownStore.close();
    trackFn({
        eventName: renderCartButton ? 'Open Cart' : 'Open Menu',
        properties: {
            currentPage: getUrlPath(),
            menuId: id,
            menuText: title,
            menuCategory: title,
            menuLocation: getLocation(flyoutToggleEl),
            navDeviceType: getNavDeviceType(flyoutToggleEl),
        },
    });
}
// Close the flyout
function close() {
    if (!isOpen) {
        return;
    }
    activeFlyoutStore.close(id);
    trackFn({
        eventName: renderCartButton ? 'Close Cart' : 'Close Menu',
        properties: {
            currentPage: getUrlPath(),
            menuId: id,
            menuText: title,
            menuCategory: title,
            menuLocation: getLocation(flyoutToggleEl),
            navDeviceType: getNavDeviceType(flyoutToggleEl),
        },
    });
}
// Toggle the flyout
function toggle() {
    isOpen ? close() : open();
}
</script>

<div
  bind:this={flyoutToggleEl}
  data-entry-id={id}
  class="FlyoutToggle"
  class:isOpen
  use:hoverWithDelay={[open, close, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION]}
  use:onClickOutside={close}
>
  {#if renderCartButton}
    <Toggle {isOpen} {title} on:toggle={toggle} />
  {:else}
    <button
      class="toggle-button"
      aria-haspopup="true"
      aria-expanded={isOpen}
      aria-controls={`nav-flyout-${id}`}
      use:onTouchOrClick={toggle}
      use:blurOnClick
    >
      {title}
      {#if enableChevron}
        <Chevron {isOpen} />
      {/if}
    </button>
  {/if}
  <Flyout id={renderCartButton ? GLOBAL_NAV_DESKTOP_CART_CONTENT_ID : id} {isOpen} {alignment}>
    {@render children?.()}
  </Flyout>
</div>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.FlyoutToggle {
  --color-theme: #006aff;
  --color-theme-focus: #05c;
  z-index: 1;
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
}
.FlyoutToggle.isOpen {
  z-index: 2;
}
.FlyoutToggle .toggle-button {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  padding: 18px 10px;
}
.FlyoutToggle .toggle-button:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.FlyoutToggle .toggle-button:focus {
  background: transparent;
  outline: none;
}
.FlyoutToggle .toggle-button:hover {
  background: transparent;
}</style>
